import * as React from 'react';
import { getImageUrl, getItemUrl } from '@/utils/urls';
import styled, { css } from 'styled-components';
import CardStatusBanner from './CardStatusBanner';
import { getItem } from '@/redux/modules/item';
import ImageSlider from './ImageSlider';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import ImageWithHover from '@/components/ImageWithHover/ImageWithHover';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import usePrevious from '@/hooks/usePrevious';
import { useSelector } from 'react-redux';

type Props = {
    itemId: number;
    noWidthLimit?: boolean;
    openOnBlank?: boolean;
    showBidStatus?: boolean;
    showImageBorder?: boolean;
    showImageSlider?: boolean;
    showImageWithHover?: boolean;
};

const ImageRow = (props: Props) => {
    const {
        itemId,
        noWidthLimit,
        openOnBlank,
        showBidStatus,
        showImageBorder,
        showImageSlider,
        showImageWithHover = false,
    } = props;

    const [showSlider, setShowSlider] = React.useState(false);
    const [hideArrows, setHideArrows] = React.useState(false);

    React.useEffect(() => {
        if (!showSlider && showImageSlider) {
            setShowSlider(true);
        }
    }, [showImageSlider, showSlider]);

    const previousShowImageSlider = usePrevious(showImageSlider);
    React.useEffect(() => {
        if (previousShowImageSlider && !showImageSlider) {
            setHideArrows(true);
        } else if (showImageSlider && !previousShowImageSlider) {
            setHideArrows(false);
        }
    }, [previousShowImageSlider, showImageSlider]);

    const { catalogId, imageVersion, isSEOExcluded, photo, photos, sellerId, title } = useSelector(
        (state) => getItem(state, itemId),
        isEqual
    );

    const firstPhoto = photo || photos?.[0] || 0;

    const itemUrl = getItemUrl(itemId, title);

    const imageOptions: any = {
        catalogId,
        imageNumber: firstPhoto,
        imageVersion,
        itemId,
        sellerId,
    };

    if (noWidthLimit) {
        imageOptions.height = 170;
    } else {
        imageOptions.width = 310;
    }

    const imageUrl = getImageUrl(imageOptions);
    const imagePlaceholder = getImageUrl({ ...imageOptions, quality: 15 });

    // get 2nd photo info
    let secondaryImageUrl, secondPhoto;
    if (photos && photos.length > 1) {
        secondPhoto = firstPhoto === photos[0] ? photos[1] : photos[0];
    }

    if (Boolean(secondPhoto)) {
        const secondImageOptions = Object.assign({}, imageOptions);
        secondImageOptions.imageNumber = secondPhoto;
        secondaryImageUrl = getImageUrl(secondImageOptions);
    }

    const targetProps = openOnBlank
        ? { rel: isSEOExcluded ? 'nofollow' : '', route: false, target: '_blank' }
        : { rel: isSEOExcluded ? 'nofollow' : '', route: true };

    let cardImage = (
        <StyledImageWithFallback
            $noWidthLimit={noWidthLimit}
            alt={title}
            placeholder={imagePlaceholder}
            src={imageUrl}
        />
    );

    if (showSlider) {
        cardImage = <StyledImageSlider hideArrows={hideArrows} itemId={itemId} />;
    } else if (Boolean(showImageWithHover) && Boolean(secondaryImageUrl)) {
        cardImage = (
            <ImageWithHover
                alt={title}
                placeholder={imagePlaceholder}
                srcPrimary={imageUrl}
                srcSecondary={secondaryImageUrl}
            />
        );
    }

    return (
        <ContainerLink $noWidthLimit={noWidthLimit} $showImageBorder={showImageBorder} {...targetProps} to={itemUrl}>
            <ImageContainerDiv $noWidthLimit={noWidthLimit}>
                <ImageWithHoverDiv $noWidthLimit={noWidthLimit}>{cardImage}</ImageWithHoverDiv>
                {showBidStatus && <CardStatusBanner itemId={itemId} />}
            </ImageContainerDiv>
        </ContainerLink>
    );
};

export default React.memo<Props>(ImageRow);

const ContainerLink = styled(Link)<{ $noWidthLimit: boolean; $showImageBorder: boolean }>`
    text-align: center;
    cursor: pointer;
    display: block;
    margin: 0 auto 7px;

    /* width */
    ${({ $noWidthLimit }) =>
        $noWidthLimit &&
        css`
            width: fit-content;
        `}

    @media print {
        max-width: 100px;
        max-height: 100px;
        width: unset;
        margin-right: 10px;
    }

    /* border */
    ${({ $showImageBorder, theme }) =>
        $showImageBorder &&
        css`
            border: 1px solid ${theme.colors.grey400};
        `}

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 12px;
    }
`;

const ImageContainerDiv = styled.div<{ $noWidthLimit: boolean }>`
    width: 100%;
    box-sizing: border-box;
    text-decoration: none;
    position: relative;
    overflow: hidden;
    min-width: 126px;
    max-width: 500px;
    transition: all 0.25s;
    ${({ $noWidthLimit }) =>
        $noWidthLimit
            ? css`
                  height: 170px;
                  width: auto;
              `
            : css`
                  padding-bottom: 100%;
              `}
`;

const ImageWithHoverDiv = styled.div<{ $noWidthLimit: boolean }>`
    ${({ $noWidthLimit }) =>
        $noWidthLimit &&
        css`
            height: 170px;
            width: auto;
            display: flex;
            justify-content: center;
        `}
`;

const StyledImageWithFallback = styled(ImageWithFallback)<{ $noWidthLimit: boolean }>`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: auto;
    display: block;
    max-height: 100%;
    max-width: 100%;
    filter: blur(0);

    @media print {
        margin: 0;
    }

    ${({ $noWidthLimit }) =>
        $noWidthLimit &&
        css`
            margin: auto;
            position: relative;
            display: block;
            filter: blur(0);
            width: auto;
            max-width: 500px;
            max-height: 170px;
        `}
`;

const StyledImageSlider = styled(ImageSlider)`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: auto;
    display: block;
    max-height: 100%;
    max-width: 100%;

    @media print {
        margin: 0;
    }
`;
